/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getHygraphCosmosPageData } from "@cosmos/hygraph-data";
import { CosmosPageSearchMetadataQuery, Locale, PageSearchFiltersFragment } from "@cosmos/hygraph-schema";
import { getHygraphLocale, getSlugParam } from "@cosmos/next-common";
import { isError } from "@epic-mw/error-types";
import { Metadata } from "next";

import { CosmosPageMetadata } from "src/common";

export const getPageFilters = async (
	locale: string = Locale.En,
	slug: string,
	previewParam?: string | string[],
	endpoint?: string,
): Promise<PageSearchFiltersFragment | null> => {
	if (!slug) {
		throw new Error("Missing slug");
	}
	const preview = previewParam && typeof previewParam === "string" ? previewParam : undefined;

	const pageData = await getHygraphCosmosPageData(
		"CosmosPageSearchFilters",
		{
			locale: [locale, Locale.En] as Locale[],
			slug,
		},
		preview,
		endpoint,
	);

	return pageData.cosmosPageSearch || null;
};

export const getPageMetadata = async (
	locale: string = Locale.En,
	slug: string,
	previewParam?: string | string[],
	endpoint?: string,
): Promise<CosmosPageSearchMetadataQuery> => {
	if (!slug) {
		throw new Error("Missing slug");
	}
	const preview = previewParam && typeof previewParam === "string" ? previewParam : undefined;

	const pageData = await getHygraphCosmosPageData(
		"CosmosPageSearchMetadata",
		{
			locale: [locale, Locale.En] as Locale[],
			slug,
		},
		preview,
		endpoint,
	);

	return pageData;
};

export async function cosmosPageSearchMetadata({
	slug: rootSlug = "",
	params,
	searchParams,
	hygraphLocales,
	getAppMetadata,
	hygraphEndpoint,
}: CosmosPageMetadata): Promise<Metadata> {
	const slugParam = getSlugParam(params);
	const slug = `${rootSlug}${slugParam}`;
	const locale = params.lang;
	let pageData: CosmosPageSearchMetadataQuery = {};
	try {
		pageData = await getPageMetadata(
			getHygraphLocale(hygraphLocales, locale),
			slug,
			searchParams.preview,
			hygraphEndpoint,
		);
	} catch (e) {
		console.error("Failed to get cosmosPageSearchMetadata()", { error: isError(e) && e.message });
	}
	const metaTags = pageData.cosmosPageSearch?.metaTags || {};

	return getAppMetadata(metaTags, slug, locale);
}
